/**
 * ============================================================================
 * SAT CHAT WIDGET - Sorpresas a Tiempo
 * Asistente virtual IA embebido en landing page
 * 
 * Funcionalidades:
 * - Burbuja flotante + panel de chat
 * - Streaming de respuestas desde Cloudflare Worker
 * - Integración "Lo quiero 💬" con contexto de producto
 * - Subida de fotos para personalización
 * - Renderizado de tarjetas de producto en el chat
 * - Opciones rápidas (botones Sí/No)
 * - Historial de sesión (sessionStorage)
 * - Indicador de escritura
 * - Auto-resize del textarea
 * - Responsive (mobile fullscreen)
 * ============================================================================
 */

(function () {
  'use strict';

  /* -----------------------------------------------------------------------
     CONFIGURACIÓN
     ----------------------------------------------------------------------- */
  const CONFIG = {
    workerUrl: window.SAT_CONFIG?.workerUrl || 'https://sat-assistant.workers.dev',
    shopName: window.SAT_CONFIG?.shopName || 'Sorpresas a Tiempo',
    whatsappUrl: window.SAT_CONFIG?.whatsappUrl || 'https://wa.me/573212304451',
    welcomeMessage: '¡Hola! 👋 Soy el asesor virtual de Sorpresas a Tiempo. ¿Quieres sorprender a alguien especial? Cuéntame qué tienes en mente y te ayudo a elegir la sorpresa perfecta 🎁',
    inputPlaceholder: 'Escribe tu mensaje...',
    maxFileSize: 20 * 1024 * 1024, // 5MB
    allowedImageTypes: ['image/jpeg', 'image/png', 'image/webp'],
    sessionKey: 'sat_chat_session',
    historyKey: 'sat_chat_history',
    autoOpenDelay: null, // ms para auto-abrir (null = no auto-abrir)
  };

  /* -----------------------------------------------------------------------
     ESTADO
     ----------------------------------------------------------------------- */
  const state = {
    isOpen: false,
    isLoading: false,
    sessionId: null,
    messages: [],        // { role: 'user'|'bot', text: '', html: '', products: [], options: [] }
    pendingPhoto: null,  // { base64, filename, mimeType }
  };

  /* -----------------------------------------------------------------------
     UTILIDADES
     ----------------------------------------------------------------------- */
  function generateSessionId() {
    return 'sat_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
  }

  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function formatBotText(text) {
    // Convertir markdown y URLs a HTML
    let html = escapeHtml(text);

    // 0. Imágenes markdown ![alt](url) → img tag (ANTES de links para no confundir)
    html = html.replace(/!\[([^\]]*)\]\((https?:\/\/[^\s\)]+)\)/g, function(match, alt, url) {
      return '<img src="' + url + '" alt="' + alt + '" class="sat-chat-img" loading="lazy">';
    });

    // 1. Links markdown [texto](url)
    html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^\s\)]+)\)/g, function(match, linkText, url) {
      return buildLink(linkText, url);
    });

    // 2. URLs sueltas (no dentro de un href ya creado)
    // Detecta URLs que NO están ya dentro de un tag HTML
    html = html.replace(/(^|[^"=])(https?:\/\/[^\s<]+)/g, function(match, prefix, url) {
      // Limpiar caracteres finales que no son parte de la URL
      var cleanUrl = url.replace(/[.,;:!?)]+$/, '');
      return prefix + buildLink(cleanUrl, cleanUrl);
    });

    // 3. Negritas **texto** y __texto__
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');
    // 4. Separadores ---
    html = html.replace(/\n---\n/g, '<hr class="sat-chat-hr">');
    html = html.replace(/^---$/gm, '<hr class="sat-chat-hr">');
    // 5. Saltos de línea
    html = html.replace(/\n/g, '<br>');
    return html;
  }

  function buildLink(linkText, url) {
    // Enlace de pago (checkout/invoice)
    if (url.includes('invoice') || url.includes('checkout')) {
      return '<a href="' + url + '" target="_blank" rel="noopener" class="sat-chat-link sat-chat-link--cta">Ir a pagar →</a>';
    }
    // WhatsApp
    if (url.includes('wa.me') || url.includes('whatsapp')) {
      var waLabel = (linkText === url) ? 'Contactar por WhatsApp' : linkText;
      return '<a href="' + url + '" target="_blank" rel="noopener" class="sat-chat-link sat-chat-link--wa">📱 ' + waLabel + '</a>';
    }
    // Link genérico
    var label = linkText;
    // Si el texto del link es una URL larga, acortarla visualmente
    if (label.length > 50 && label.startsWith('http')) {
      label = 'Abrir enlace ↗';
    }
    return '<a href="' + url + '" target="_blank" rel="noopener" class="sat-chat-link">' + label + '</a>';
  }

  function scrollToBottom() {
    const container = document.getElementById('sat-chat-messages');
    if (container) {
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
    }
  }

  function saveSession() {
    try {
      sessionStorage.setItem(CONFIG.sessionKey, state.sessionId);
      sessionStorage.setItem(CONFIG.historyKey, JSON.stringify(state.messages));
    } catch (e) { /* silencioso si no hay sessionStorage */ }
  }

  function loadSession() {
    try {
      const sid = sessionStorage.getItem(CONFIG.sessionKey);
      const hist = sessionStorage.getItem(CONFIG.historyKey);
      if (sid) state.sessionId = sid;
      if (hist) state.messages = JSON.parse(hist);
    } catch (e) { /* silencioso */ }
  }

  /* -----------------------------------------------------------------------
     INYECTAR HTML DEL WIDGET
     ----------------------------------------------------------------------- */
  function injectHTML() {
    const widget = document.createElement('div');
    widget.id = 'sat-chat-widget';
    widget.innerHTML = `
      <!-- BURBUJA FLOTANTE -->
      <button class="sat-chat-bubble" id="sat-chat-bubble"
              aria-label="Abrir chat con asesor virtual"
              type="button">
        <svg id="sat-bubble-icon-chat" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        <svg id="sat-bubble-icon-close" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:none">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
        <span class="sat-chat-bubble__label" id="sat-bubble-label">Te asesoro</span>
        <span class="sat-chat-bubble__badge" id="sat-bubble-badge"></span>
      </button>

      <!-- PANEL DE CHAT -->
      <div class="sat-chat-panel" id="sat-chat-panel" role="dialog" aria-label="Chat con asesor virtual">
        
        <!-- Header -->
        <div class="sat-chat-header">
          <div class="sat-chat-header__avatar">🎁</div>
          <div class="sat-chat-header__info">
            <div class="sat-chat-header__name">Asesor Virtual</div>
            <div class="sat-chat-header__status" id="sat-chat-status">En línea</div>
          </div>
          <button class="sat-chat-header__close" id="sat-chat-close" type="button" aria-label="Cerrar chat">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <!-- Mensajes -->
        <div class="sat-chat-messages" id="sat-chat-messages" aria-live="polite">
          <!-- Los mensajes se renderizan aquí dinámicamente -->
        </div>

        <!-- Preview de foto (oculto por defecto) -->
        <div class="sat-chat-photo-bar" id="sat-photo-bar" style="display:none">
          <div class="sat-chat-photo-bar__preview">
            <img id="sat-photo-preview-img" src="" alt="Preview de foto">
            <button class="sat-chat-photo-bar__remove" id="sat-photo-remove" type="button" aria-label="Quitar foto">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          <span class="sat-chat-photo-bar__name" id="sat-photo-name"></span>
        </div>

        <!-- Input -->
        <div class="sat-chat-input-area">
          <button class="sat-chat-btn-attach" id="sat-btn-attach" type="button" aria-label="Adjuntar foto">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
            </svg>
          </button>
          <textarea
            class="sat-chat-input"
            id="sat-chat-input"
            placeholder="${CONFIG.inputPlaceholder}"
            rows="1"
            aria-label="Escribe tu mensaje"></textarea>
          <button class="sat-chat-btn-send" id="sat-btn-send" type="button" aria-label="Enviar mensaje" disabled>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
            </svg>
          </button>
        </div>

      </div>

      <!-- Input oculto para fotos -->
      <input type="file" id="sat-file-input" accept="image/jpeg,image/png,image/webp" style="display:none">
    `;

    // Estilos adicionales para photo bar (inline para no requerir otro CSS)
    const style = document.createElement('style');
    style.textContent = `
      .sat-chat-photo-bar {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 1rem;
        background: var(--sat-bg-alt, #f9f5f3);
        border-top: 1px solid var(--sat-border, #ebe5e1);
      }
      .sat-chat-photo-bar__preview {
        position: relative;
        width: 48px;
        height: 48px;
        border-radius: 8px;
        overflow: hidden;
        flex-shrink: 0;
      }
      .sat-chat-photo-bar__preview img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      .sat-chat-photo-bar__remove {
        position: absolute;
        top: -4px;
        right: -4px;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #e74c3c;
        color: white;
        border: 2px solid white;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        padding: 0;
      }
      .sat-chat-photo-bar__name {
        font-size: 0.78rem;
        color: var(--sat-text-light, #7a7588);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    `;

    /* ── Formulario de datos de entrega inline ── */
    style.textContent += `
      .sat-form-wrapper {
        margin-top: 0.6rem;
        border: 1px solid #e0d6ea;
        border-radius: 12px;
        overflow: hidden;
        background: #faf8fc;
      }
      .sat-form-section {
        padding: 0.6rem 0.75rem 0.5rem;
      }
      .sat-form-section + .sat-form-section {
        border-top: 1px solid #e8e0ee;
      }
      .sat-form-section__title {
        font-size: 0.72rem;
        font-weight: 700;
        color: #5b2d6e;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        margin-bottom: 0.4rem;
        display: flex;
        align-items: center;
        gap: 0.3rem;
      }
      .sat-form-row {
        margin-bottom: 0.4rem;
      }
      .sat-form-row:last-child {
        margin-bottom: 0;
      }
      .sat-form-label {
        display: block;
        font-size: 0.7rem;
        color: #6b5e7a;
        margin-bottom: 0.15rem;
        font-weight: 600;
      }
      .sat-form-input {
        width: 100%;
        padding: 0.45rem 0.55rem;
        border: 1px solid #d6cce0;
        border-radius: 8px;
        font-size: 16px;  /* ≥16px prevents iOS Safari auto-zoom on focus */
        font-family: inherit;
        background: white;
        color: #2d1f3d;
        outline: none;
        transition: border-color 0.2s;
        box-sizing: border-box;
        -webkit-appearance: none; /* Prevent iOS default styling */
      }
      .sat-form-input:focus {
        border-color: #9b59b6;
        box-shadow: 0 0 0 2px rgba(155,89,182,0.15);
      }
      .sat-form-input--error {
        border-color: #e74c3c !important;
        background: #fdf0ef;
      }
      .sat-form-error {
        font-size: 0.65rem;
        color: #e74c3c;
        margin-top: 0.1rem;
        display: none;
      }
      .sat-form-row--inline {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0.4rem;
      }
      .sat-form-submit {
        display: block;
        width: calc(100% - 1.5rem);
        margin: 0.5rem auto 0.6rem;
        padding: 0.65rem;
        background: linear-gradient(135deg, #d4536a, #c0392b);
        color: white;
        border: none;
        border-radius: 10px;
        font-size: 0.88rem;
        font-weight: 700;
        font-family: inherit;
        cursor: pointer;
        transition: opacity 0.2s, transform 0.1s;
      }
      .sat-form-submit:hover {
        opacity: 0.92;
      }
      .sat-form-submit:active {
        transform: scale(0.98);
      }
      .sat-form-submit:disabled {
        opacity: 0.5;
        cursor: default;
        transform: none;
      }
      .sat-form-note {
        font-size: 0.65rem;
        color: #8e82a0;
        text-align: center;
        margin: 0 0.75rem 0.5rem;
      }
      .sat-form-add-recipient {
        display: block;
        width: calc(100% - 1.5rem);
        margin: 0.5rem 0.75rem;
        padding: 0.5rem;
        background: transparent;
        color: var(--sat-primary, #d4536a);
        border: 1.5px dashed var(--sat-primary, #d4536a);
        border-radius: 8px;
        font-size: 0.8rem;
        font-weight: 600;
        cursor: pointer;
        transition: background 0.2s;
      }
      .sat-form-add-recipient:hover {
        background: rgba(212, 83, 106, 0.08);
      }
      .sat-form-remove-recipient {
        float: right;
        background: none;
        border: none;
        color: #c0392b;
        font-size: 1rem;
        cursor: pointer;
        padding: 0 0.3rem;
        line-height: 1;
        opacity: 0.6;
        transition: opacity 0.2s;
      }
      .sat-form-remove-recipient:hover {
        opacity: 1;
      }
      .sat-form-recipient {
        position: relative;
      }
    `;

    document.body.appendChild(style);
    document.body.appendChild(widget);
  }

  /* -----------------------------------------------------------------------
     RENDERIZAR MENSAJES
     ----------------------------------------------------------------------- */
  function renderMessage(msg) {
    const container = document.getElementById('sat-chat-messages');
    if (!container) return;

    const wrapper = document.createElement('div');

    if (msg.role === 'user') {
      wrapper.className = 'sat-msg sat-msg--user';
      wrapper.innerHTML = escapeHtml(msg.text);

      // Si tiene foto adjunta, mostrar preview
      if (msg.photo) {
        const photoDiv = document.createElement('div');
        photoDiv.className = 'sat-msg-photo-preview';
        photoDiv.innerHTML = `<img src="${msg.photo}" alt="Foto adjunta">`;
        wrapper.appendChild(photoDiv);
      }

    } else if (msg.role === 'bot') {
      wrapper.className = 'sat-msg sat-msg--bot';
      wrapper.innerHTML = msg.html || formatBotText(msg.text || '');

      // Si tiene productos, renderizar tarjetas
      if (msg.products && msg.products.length > 0) {
        const productsDiv = document.createElement('div');
        productsDiv.className = 'sat-msg-products';
        msg.products.forEach(p => {
          productsDiv.innerHTML += `
            <div class="sat-msg-product-card" onclick="window.SATChat.abrirConDatos('${escapeHtml(p.title)}', '${escapeHtml(p.price)}', '${escapeHtml(p.variantId || '')}', '${escapeHtml(p.image || '')}')">
              ${p.image ? `<img src="${escapeHtml(p.image)}" alt="${escapeHtml(p.title)}" loading="lazy">` : ''}
              <div class="sat-msg-product-card__info">
                <p class="sat-msg-product-card__name">${escapeHtml(p.title)}</p>
                <p class="sat-msg-product-card__price">${escapeHtml(p.price)}</p>
              </div>
            </div>
          `;
        });
        wrapper.appendChild(productsDiv);
      }

      // Si tiene opciones rápidas, renderizar botones
      if (msg.options && msg.options.length > 0) {
        const optionsDiv = document.createElement('div');
        optionsDiv.className = 'sat-chat-options';
        msg.options.forEach(opt => {
          const btn = document.createElement('button');
          btn.className = 'sat-chat-option-btn';
          btn.textContent = opt.label;
          btn.type = 'button';
          btn.addEventListener('click', function () {
            // Deshabilitar todos los botones del grupo
            optionsDiv.querySelectorAll('button').forEach(b => {
              b.disabled = true;
              b.style.opacity = '0.5';
              b.style.cursor = 'default';
            });
            btn.style.opacity = '1';
            btn.style.background = 'var(--sat-primary, #d4536a)';
            btn.style.color = 'white';
            // Enviar como mensaje del usuario
            sendMessage(opt.value || opt.label);
          });
          optionsDiv.appendChild(btn);
        });
        wrapper.appendChild(optionsDiv);
      }

      // UX v2: Agregar opciones clickeables si hay lista de productos en texto
      if (msg.text && (!msg.options || msg.options.length === 0)) {
        addClickableOptions(wrapper, msg.text);
      }
    }

    container.appendChild(wrapper);
    scrollToBottom();
  }

  function renderAllMessages() {
    const container = document.getElementById('sat-chat-messages');
    if (!container) return;
    container.innerHTML = '';
    state.messages.forEach(msg => renderMessage(msg));
  }

  function showTypingIndicator() {
    const container = document.getElementById('sat-chat-messages');
    if (!container) return;

    // Remover indicador previo si existe
    hideTypingIndicator();

    const typing = document.createElement('div');
    typing.className = 'sat-typing';
    typing.id = 'sat-typing-indicator';
    typing.innerHTML = `
      <div class="sat-typing__dot"></div>
      <div class="sat-typing__dot"></div>
      <div class="sat-typing__dot"></div>
    `;
    container.appendChild(typing);
    scrollToBottom();

    // Actualizar status
    const statusEl = document.getElementById('sat-chat-status');
    if (statusEl) statusEl.textContent = 'Escribiendo...';
  }

  function hideTypingIndicator() {
    const el = document.getElementById('sat-typing-indicator');
    if (el) el.remove();

    const statusEl = document.getElementById('sat-chat-status');
    if (statusEl) statusEl.textContent = 'En línea';
  }

  /* -----------------------------------------------------------------------
     AGREGAR MENSAJE AL ESTADO
     ----------------------------------------------------------------------- */
  function addMessage(role, text, extras = {}) {
    const msg = { role, text, ...extras };
    state.messages.push(msg);
    renderMessage(msg);
    saveSession();
    return msg;
  }

  /* -----------------------------------------------------------------------
     ENVIAR MENSAJE AL WORKER
     ----------------------------------------------------------------------- */
  async function sendMessage(text) {
    if (state.isLoading || (!text.trim() && !state.pendingPhoto)) return;

    const userText = text.trim();
    state.isLoading = true;
    updateSendButton();

    // Agregar mensaje del usuario
    const userMsg = { text: userText };

    // Capturar foto ANTES de limpiar (para que se envíe al Worker)
    let capturedPhoto = null;
    if (state.pendingPhoto) {
      capturedPhoto = {
        base64: state.pendingPhoto.base64,
        filename: state.pendingPhoto.filename,
        mimeType: state.pendingPhoto.mimeType,
      };
      userMsg.photo = state.pendingPhoto.base64Preview; // URL para preview en chat
    }
    addMessage('user', userText, userMsg.photo ? { photo: userMsg.photo } : {});

    // Limpiar input y foto (ya capturamos los datos arriba)
    const input = document.getElementById('sat-chat-input');
    if (input) {
      input.value = '';
      input.style.height = 'auto';
    }
    clearPhotoPreview();

    // Mostrar typing
    showTypingIndicator();

    try {
      // Construir payload
      const payload = {
        sessionId: state.sessionId,
        message: userText,
      };

      // Incluir foto capturada
      if (capturedPhoto) {
        payload.archivo = capturedPhoto;
      }

      const response = await fetch(`${CONFIG.workerUrl}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      // Intentar streaming si el response es stream
      const contentType = response.headers.get('content-type') || '';

      if (contentType.includes('text/event-stream') || contentType.includes('text/plain')) {
        // Streaming SSE
        await handleStreamingResponse(response);
      } else {
        // Respuesta JSON normal
        const data = await response.json();
        hideTypingIndicator();
        handleJsonResponse(data);
      }

    } catch (error) {
      console.error('SAT Chat error:', error);
      hideTypingIndicator();
      addMessage('bot', 'Disculpa, tuve un problema de conexión. ¿Puedes intentar de nuevo? Si prefieres, escríbenos por WhatsApp.', {
        options: [
          { label: 'Reintentar', value: userText },
          { label: 'Ir a WhatsApp', value: '__whatsapp__' },
        ]
      });
    }

    state.isLoading = false;
    updateSendButton();

    /* Mobile: mantener focus en input para que el usuario siga escribiendo */
    const inputAfter = document.getElementById('sat-chat-input');
    if (inputAfter && state.isOpen) {
      inputAfter.focus();
    }
  }

  /* -----------------------------------------------------------------------
     MANEJAR RESPUESTA STREAMING (SSE)
     ----------------------------------------------------------------------- */
  async function handleStreamingResponse(response) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let botText = '';
    let botMsgEl = null;

    hideTypingIndicator();

    // Crear elemento de mensaje bot vacío
    const container = document.getElementById('sat-chat-messages');
    botMsgEl = document.createElement('div');
    botMsgEl.className = 'sat-msg sat-msg--bot';
    botMsgEl.innerHTML = '';
    container.appendChild(botMsgEl);

    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Procesar líneas SSE
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Última línea puede estar incompleta

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);

          if (data === '[DONE]') {
            // Streaming terminó
            continue;
          }

          try {
            const parsed = JSON.parse(data);

            if (parsed.type === 'text_delta' || parsed.type === 'content_block_delta') {
              // Fragmento de texto
              const delta = parsed.delta?.text || parsed.text || '';
              botText += delta;
              botMsgEl.innerHTML = formatBotText(botText);
              scrollToBottom();

            } else if (parsed.type === 'message_complete' || parsed.type === 'response') {
              // Mensaje completo (fallback si el worker envía todo al final)
              botText = parsed.text || parsed.content || botText;
              botMsgEl.innerHTML = formatBotText(botText);

              // Procesar extras (productos, opciones)
              if (parsed.products) {
                renderProductsInMessage(botMsgEl, parsed.products);
              }
              if (parsed.options) {
                renderOptionsInMessage(botMsgEl, parsed.options);
              }
              if (parsed.checkoutUrl) {
                renderCheckoutButton(botMsgEl, parsed.checkoutUrl);
              }

            } else if (parsed.type === 'error') {
              botText = parsed.message || 'Ocurrió un error inesperado.';
              botMsgEl.innerHTML = formatBotText(botText);
            }

          } catch (e) {
            // Si no es JSON, tratar como texto plano
            botText += data;
            botMsgEl.innerHTML = formatBotText(botText);
            scrollToBottom();
          }
        }
      }
    }

    // Guardar mensaje en estado
    if (botText) {
      state.messages.push({ role: 'bot', text: botText });
      saveSession();

      // UX v2: Agregar opciones clickeables si hay lista de productos
      addClickableOptions(botMsgEl, botText);
    }

    scrollToBottom();
  }

  /* -----------------------------------------------------------------------
     MANEJAR RESPUESTA JSON (no streaming)
     ----------------------------------------------------------------------- */
  function handleJsonResponse(data) {
    const extras = {};

    if (data.products && data.products.length > 0) {
      extras.products = data.products;
    }

    if (data.options && data.options.length > 0) {
      extras.options = data.options;
    }

    const text = data.text || data.content || data.message || '';
    const botMsg = addMessage('bot', text, extras);

    // Si hay checkout URL, agregar como opción
    if (data.checkoutUrl) {
      const container = document.getElementById('sat-chat-messages');
      const lastMsg = container.lastElementChild;
      if (lastMsg) {
        renderCheckoutButton(lastMsg, data.checkoutUrl);
      }
    }
  }

  /* -----------------------------------------------------------------------
     RENDERIZADORES AUXILIARES PARA CHAT
     ----------------------------------------------------------------------- */
  function renderProductsInMessage(msgEl, products) {
    const productsDiv = document.createElement('div');
    productsDiv.className = 'sat-msg-products';
    products.forEach(p => {
      productsDiv.innerHTML += `
        <div class="sat-msg-product-card" onclick="window.SATChat.abrirConDatos('${escapeHtml(p.title || '')}', '${escapeHtml(p.price || '')}', '${escapeHtml(p.variantId || '')}', '${escapeHtml(p.image || '')}')">
          ${p.image ? `<img src="${escapeHtml(p.image)}" alt="${escapeHtml(p.title || '')}" loading="lazy">` : ''}
          <div class="sat-msg-product-card__info">
            <p class="sat-msg-product-card__name">${escapeHtml(p.title || '')}</p>
            <p class="sat-msg-product-card__price">${escapeHtml(p.price || '')}</p>
          </div>
        </div>
      `;
    });
    msgEl.appendChild(productsDiv);
  }

  function renderOptionsInMessage(msgEl, options) {
    const optionsDiv = document.createElement('div');
    optionsDiv.className = 'sat-chat-options';
    options.forEach(opt => {
      const btn = document.createElement('button');
      btn.className = 'sat-chat-option-btn';
      btn.textContent = opt.label;
      btn.type = 'button';
      btn.addEventListener('click', function () {
        optionsDiv.querySelectorAll('button').forEach(b => {
          b.disabled = true;
          b.style.opacity = '0.5';
          b.style.cursor = 'default';
        });
        btn.style.opacity = '1';
        btn.style.background = 'var(--sat-primary, #d4536a)';
        btn.style.color = 'white';

        if (opt.value === '__whatsapp__') {
          window.open(CONFIG.whatsappUrl, '_blank');
        } else {
          sendMessage(opt.value || opt.label);
        }
      });
      optionsDiv.appendChild(btn);
    });
    msgEl.appendChild(optionsDiv);
  }

  function renderCheckoutButton(msgEl, checkoutUrl) {
    const btnDiv = document.createElement('div');
    btnDiv.style.marginTop = '0.75rem';
    btnDiv.innerHTML = `
      <a href="${escapeHtml(checkoutUrl)}" target="_blank" rel="noopener"
         class="sat-chat-option-btn"
         style="display:inline-flex;align-items:center;gap:0.4rem;background:var(--sat-primary,#d4536a);color:white;border-color:var(--sat-primary,#d4536a);text-decoration:none;padding:0.65rem 1.25rem;font-weight:700;">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
        Ir a pagar
      </a>
    `;
    msgEl.appendChild(btnDiv);
  }

  /* -----------------------------------------------------------------------
     OPCIONES CLICKEABLES EN CHAT (UX v3-tested)
     Testeado contra 9 patrones reales del bot. Detecta:
     1. PRODUCTOS: numerados con precio (misma línea o siguiente)
     2. MOTIVOS: numerados o bullets SIN precio, con contexto "motivo/ocasión"
     3. HORARIOS: con patrones de hora (AM/PM)
     EXCLUYE: listas de datos personales (cuando los ÍTEMS son campos)
     ----------------------------------------------------------------------- */
  function addClickableOptions(msgEl, botText) {
    try {
      /* ── 0. FORMULARIO DE DATOS (prioridad máxima) ── */
      if (_detectFormTrigger(botText)) {
        /* Eliminar el marcador [FORMULARIO_DATOS] del texto visible SIN destruir el DOM.
           innerHTML = ... destruiría los event listeners del form, así que
           recorremos nodos de texto y quitamos solo el marcador. */
        _removeTextMarker(msgEl, /\[FORMULARIO_DATOS\]/gi);
        _renderDeliveryForm(msgEl);
        return;
      }

      var lines = botText.split('\n');
      var priceRx = /\$[\d.,]+|\d[\d.,]+\s*COP/i;
      var timeRx = /\d{1,2}:\d{2}\s*(AM|PM|am|pm)/i;
      var motivoContextRx = /motivo|ocasi[oó]n|celebr/i;

      /* ── EXCLUSIÓN: mensajes de confirmación/resumen de pedido ── */
      var lower = botText.toLowerCase();
      var isConfirmation = (
        /confirm[aá]s?\b/.test(lower) && /pedido|orden|correcto/.test(lower)
      ) || (
        /resumen\s*(de\s*(l[ao]?\s*)?)?(tu\s*)?(pedido|orden|compra)/i.test(lower)
      ) || (
        /crear\s*(el\s*)?pedido/i.test(lower) && priceRx.test(botText)
      ) || (
        /todo\s*(est[aá])?\s*correcto/i.test(lower) && priceRx.test(botText)
      ) || (
        /enlace\s*(de\s*)?(pago|checkout)/i.test(lower)
      );
      if (isConfirmation) return;
      /* Data fields: solo excluir cuando los ÍTEMS son campos de formulario */
      var dataFieldRx = /^\*{0,2}(nombre|apellido|direcci[oó]n|ciudad|tel[eé]fono|correo|c[eé]dula)\*{0,2}(\s|$|\()/i;

      /* ── Recopilar ítems numerados ── */
      var numberedItems = [];
      for (var i = 0; i < lines.length; i++) {
        var line = lines[i].trim();
        var nm = line.match(/^(\d+)[.)]\s+(.+)/);
        if (!nm) continue;
        var content = nm[2];

        var hasPrice = priceRx.test(content);
        if (!hasPrice) {
          for (var j = 1; j <= 2 && (i + j) < lines.length; j++) {
            var next = lines[i + j].trim();
            if (/^\d+[.)]/.test(next)) break;
            if (priceRx.test(next)) { hasPrice = true; break; }
          }
        }
        var hasTime = timeRx.test(content);
        var name = content.replace(/\*\*/g, '').replace(/__/g, '').trim();
        var nameClean = name.split(/\s*[–—]\s*\$|\s*\(\$|\s+\$/)[0].trim();
        var isDataField = dataFieldRx.test(name);

        numberedItems.push({
          num: nm[1], name: name, nameClean: nameClean,
          hasPrice: hasPrice, hasTime: hasTime, isDataField: isDataField
        });
      }

      /* ── Recopilar ítems bullet ── */
      var bulletItems = [];
      for (var i = 0; i < lines.length; i++) {
        var line = lines[i].trim();
        var bm = line.match(/^[\-–—•]\s+(.+)/);
        if (!bm) continue;
        var label = bm[1].trim();
        if (label.length > 1 && label.length < 80) {
          bulletItems.push({ label: label, hasTime: timeRx.test(label) });
        }
      }

      /* ── EXCLUSIÓN: si la mayoría de ítems numerados son campos de datos ── */
      var dataCount = numberedItems.filter(function(it) { return it.isDataField; }).length;
      if (dataCount > 0 && dataCount >= numberedItems.length * 0.5) return;

      /* ── 1. HORARIOS (prioridad máxima — con agrupación por día) ── */
      var horarioResult = _detectGroupedHorarios(lines, timeRx);
      if (horarioResult) {
        if (horarioResult.type === 'grouped') {
          _renderGroupedButtons(msgEl, horarioResult.groups);
        } else {
          _renderButtons(msgEl, horarioResult.slots.map(function(s) {
            return { html: escapeHtml(s), value: s };
          }));
        }
        return;
      }

      /* ── 2. PRODUCTOS (numerados con precio) ── */
      var priceItems = numberedItems.filter(function(it) { return it.hasPrice; });
      if (priceItems.length >= 2) {
        _renderButtons(msgEl, priceItems.map(function(p) {
          return {
            html: '<strong>' + p.num + '.</strong> ' + escapeHtml(p.nameClean),
            value: p.nameClean
          };
        }));
        return;
      }

      /* ── 3. MOTIVOS/OPCIONES (numerados SIN precio, con contexto) ── */
      var optionItems = numberedItems.filter(function(it) {
        return !it.hasPrice && !it.hasTime && !it.isDataField;
      });
      if (optionItems.length >= 3 && motivoContextRx.test(botText)) {
        _renderButtons(msgEl, optionItems.map(function(m) {
          return { html: escapeHtml(m.name), value: m.name };
        }));
        return;
      }

      /* ── 4. MOTIVOS via bullets (formato alternativo del bot) ── */
      var nonTimeBullets = bulletItems.filter(function(b) { return !b.hasTime; });
      if (nonTimeBullets.length >= 3 && motivoContextRx.test(botText)) {
        _renderButtons(msgEl, nonTimeBullets.map(function(b) {
          return { html: escapeHtml(b.label), value: b.label };
        }));
      }

    } catch (e) {
      console.warn('[SAT Chat] addClickableOptions error:', e);
    }
  }

  /* Helper: renderiza botones clickeables debajo del mensaje */
  /* Helper: oculta las líneas de lista numerada/bullet del texto del mensaje */
  function _hideListText(msgEl) {
    // El texto del bot está directamente en msgEl como HTML con <br>
    // Buscamos el contenido HTML excluyendo divs ya añadidos (opciones, productos)
    var childNodes = msgEl.childNodes;
    for (var n = 0; n < childNodes.length; n++) {
      var node = childNodes[n];
      // Solo procesar nodos de texto o elementos inline (no divs de opciones/productos)
      if (node.nodeType === 3) continue; // text node, skip
      if (node.nodeType === 1 && node.tagName === 'DIV') continue; // skip option divs
    }

    // Obtener el HTML actual (sin los divs de opciones que se agregarán después)
    var currentHTML = msgEl.innerHTML;
    // Separar en líneas por <br>
    var parts = currentHTML.split(/<br\s*\/?>/gi);
    var kept = [];
    var listStarted = false;

    for (var i = 0; i < parts.length; i++) {
      var stripped = parts[i].replace(/<[^>]*>/g, '').trim();
      var isNumbered = /^\d+[.)]\s/.test(stripped);
      var isBullet = /^[•\-–—]\s/.test(stripped);
      var isHR = /^<hr[^>]*>$/.test(parts[i].trim());
      var isEmpty = stripped === '' || stripped === '---';

      if (isNumbered || isBullet) {
        listStarted = true;
        continue; // ocultar esta línea
      }
      if (isHR && listStarted) continue; // ocultar separadores entre items
      if (isEmpty && listStarted) continue; // ocultar líneas vacías dentro de la lista

      // Si ya empezó la lista y encontramos texto normal, es la pregunta final → mantener
      if (listStarted && stripped.length > 0 && !isNumbered && !isBullet) {
        listStarted = false;
      }
      kept.push(parts[i]);
    }

    // Limpiar <br> consecutivos al final
    var cleaned = kept.join('<br>').replace(/(<br\s*\/?>){3,}/gi, '<br><br>').replace(/(<br\s*\/?>)+$/gi, '');
    msgEl.innerHTML = cleaned;
  }

  function _renderButtons(msgEl, items) {
    // Ocultar el texto de la lista numerada, dejar solo intro y pregunta
    _hideListText(msgEl);

    var div = document.createElement('div');
    div.className = 'sat-chat-options sat-chat-options--clickable';
    div.style.cssText = 'display:flex;flex-direction:column;gap:0.35rem;margin-top:0.6rem;';

    items.forEach(function(item) {
      var btn = document.createElement('button');
      btn.className = 'sat-chat-product-option';
      btn.type = 'button';
      btn.innerHTML = item.html;
      btn.addEventListener('click', function() {
        div.querySelectorAll('button').forEach(function(b) {
          b.disabled = true;
          b.style.opacity = '0.5';
          b.style.cursor = 'default';
        });
        btn.style.opacity = '1';
        btn.style.borderColor = '#d4536a';
        btn.style.background = '#d4536a';
        btn.style.color = 'white';
        sendMessage(item.value);
      });
      div.appendChild(btn);
    });

    msgEl.appendChild(div);
  }

  /* Helper: detecta horarios agrupados por día (HOY, MAÑANA, OTRO DÍA, etc.) */
  function _detectGroupedHorarios(lines, timeRx) {
    var dayHeaderRx = /\b(HOY|MA[NÑ]ANA|PASADO\s*MA[NÑ]ANA|OTRO\s*D[IÍ]A|LUNES|MARTES|MI[EÉ]RCOLES|JUEVES|VIERNES|S[AÁ]BADO|DOMINGO)/i;
    var groups = [];
    var currentGroup = null;
    var ungroupedSlots = [];

    for (var i = 0; i < lines.length; i++) {
      var line = lines[i].trim();
      if (!line) continue;

      var cleanLine = line.replace(/\*\*/g, '').replace(/__/g, '').trim();
      var headerMatch = cleanLine.match(dayHeaderRx);

      if (headerMatch && !timeRx.test(line)) {
        var dayKeyword = headerMatch[1];
        var idxOfDay = cleanLine.toUpperCase().indexOf(dayKeyword.toUpperCase());
        var label = cleanLine.substring(idxOfDay)
          .replace(/\s*[-–—―]\s*(\d+\s*rangos?|Disponibles?).*$/i, '')
          .replace(/\s*[-–—―:]\s*$/, '')
          .replace(/:$/, '')
          .trim();
        var isOtroDia = /OTRO\s*D[IÍ]A/i.test(dayKeyword);
        currentGroup = { label: label, slots: [], isOtroDia: isOtroDia };
        groups.push(currentGroup);
        continue;
      }

      var slotMatch = line.match(/^(?:[\-–—―•]|\d+[.)])\s+(.+)/);
      if (slotMatch && timeRx.test(slotMatch[1])) {
        var slotText = slotMatch[1].replace(/\*\*/g, '').replace(/[✅❌⚠️🟢🔴]/g, '').trim();
        if (currentGroup) {
          currentGroup.slots.push(slotText);
        } else {
          ungroupedSlots.push(slotText);
        }
      }
    }

    /* Si hay grupos con slots (o grupo "Otro día" sin slots) → agrupado */
    var hasContent = groups.some(function(g) { return g.slots.length > 0 || g.isOtroDia; });
    if (groups.length > 0 && hasContent) {
      return { type: 'grouped', groups: groups };
    }
    if (ungroupedSlots.length >= 2) {
      return { type: 'flat', slots: ungroupedSlots };
    }
    return null;
  }

  /* Helper: renderiza botones de horario agrupados por día */
  function _renderGroupedButtons(msgEl, groups) {
    // Ocultar el texto de horarios, dejar solo intro
    _hideListText(msgEl);

    var wrapper = document.createElement('div');
    wrapper.className = 'sat-chat-options sat-chat-options--horarios';
    wrapper.style.cssText = 'display:flex;flex-direction:column;gap:0.5rem;margin-top:0.6rem;';

    function disableAll() {
      wrapper.querySelectorAll('button').forEach(function(b) {
        b.disabled = true;
        b.style.opacity = '0.5';
        b.style.cursor = 'default';
      });
    }

    groups.forEach(function(group) {
      /* Encabezado del día */
      var header = document.createElement('div');
      header.className = 'sat-chat-day-header';
      header.style.cssText = 'font-size:0.8rem;font-weight:700;color:#5b2d6e;padding:0.3rem 0 0.1rem;border-bottom:1px solid #e8e0ee;margin-top:0.3rem;';
      header.textContent = '📅 ' + group.label;
      wrapper.appendChild(header);

      /* "OTRO DÍA" → botón especial sin rangos */
      if (group.isOtroDia) {
        var btn = document.createElement('button');
        btn.className = 'sat-chat-product-option';
        btn.type = 'button';
        btn.textContent = '📆 Elegir otra fecha...';
        btn.style.cssText = 'font-style:italic;';
        btn.addEventListener('click', function() {
          disableAll();
          btn.style.opacity = '1';
          btn.style.borderColor = '#d4536a';
          btn.style.background = '#d4536a';
          btn.style.color = 'white';
          btn.style.fontStyle = 'normal';
          sendMessage('Quiero elegir otra fecha de entrega');
        });
        wrapper.appendChild(btn);
        return;
      }

      /* Grupo normal: botones de horario */
      if (group.slots.length === 0) return;

      group.slots.forEach(function(slot) {
        var btn = document.createElement('button');
        btn.className = 'sat-chat-product-option';
        btn.type = 'button';
        btn.textContent = slot;
        btn.addEventListener('click', function() {
          disableAll();
          btn.style.opacity = '1';
          btn.style.borderColor = '#d4536a';
          btn.style.background = '#d4536a';
          btn.style.color = 'white';
          sendMessage(group.label + ' - ' + slot);
        });
        wrapper.appendChild(btn);
      });
    });

    msgEl.appendChild(wrapper);
  }

  /* -----------------------------------------------------------------------
     FORMULARIO INLINE DE DATOS DE ENTREGA
     Detecta cuando Claude pide los datos y renderiza un formulario
     que captura los 11 campos en un solo envío.
     ----------------------------------------------------------------------- */

  /* Elimina un patrón de texto de los nodos de texto del DOM sin destruir el árbol.
     Esto preserva event listeners en otros elementos hijos. */
  function _removeTextMarker(el, regex) {
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
    var nodesToFix = [];
    while (walker.nextNode()) {
      if (regex.test(walker.currentNode.textContent)) {
        nodesToFix.push(walker.currentNode);
      }
    }
    nodesToFix.forEach(function(node) {
      node.textContent = node.textContent.replace(regex, '');
    });
  }

  /* Detecta si el bot está pidiendo datos de contacto/entrega */
  function _detectFormTrigger(botText) {
    if (!botText) return false;
    var lower = botText.toLowerCase();
    /* Trigger: el bot incluye la frase marcador que le indicamos en el prompt */
    if (lower.indexOf('[formulario_datos]') !== -1) return true;
    return false;
  }

  /* Renderiza el formulario inline dentro del mensaje del bot */
  function _renderDeliveryForm(msgEl) {
    var wrapper = document.createElement('div');
    wrapper.className = 'sat-form-wrapper';
    wrapper._recipientCount = 0;

    wrapper.innerHTML = ''
      /* ── Sección 1: Contacto ── */
      + '<div class="sat-form-section">'
      +   '<div class="sat-form-section__title">📧 Email Comprador</div>'
      +   '<div class="sat-form-row">'
      +     '<label class="sat-form-label">Correo electrónico *</label>'
      +     '<input class="sat-form-input" id="sat-f-email" type="email" placeholder="nombre@dominio.com" autocomplete="email">'
      +     '<div class="sat-form-error" id="sat-f-email-err">Formato de correo inválido</div>'
      +   '</div>'
      + '</div>'

      /* ── Contenedor de destinatarios ── */
      + '<div id="sat-recipients-container"></div>'

      /* ── Sección: Comprador ── */
      + '<div class="sat-form-section">'
      +   '<div class="sat-form-section__title">💳 Comprador (quien paga)</div>'
      +   '<div class="sat-form-row sat-form-row--inline">'
      +     '<div>'
      +       '<label class="sat-form-label">Nombre *</label>'
      +       '<input class="sat-form-input" id="sat-f-comp-nombre" type="text" placeholder="Nombre" autocomplete="given-name">'
      +     '</div>'
      +     '<div>'
      +       '<label class="sat-form-label">Apellido *</label>'
      +       '<input class="sat-form-input" id="sat-f-comp-apellido" type="text" placeholder="Apellido" autocomplete="family-name">'
      +     '</div>'
      +   '</div>'
      +   '<div class="sat-form-row">'
      +     '<label class="sat-form-label">Dirección *</label>'
      +     '<input class="sat-form-input" id="sat-f-comp-dir" type="text" placeholder="Calle 100 #15-20" autocomplete="street-address">'
      +   '</div>'
      +   '<div class="sat-form-row sat-form-row--inline">'
      +     '<div>'
      +       '<label class="sat-form-label">Ciudad *</label>'
      +       '<input class="sat-form-input" id="sat-f-comp-ciudad" type="text" placeholder="Ciudad" autocomplete="address-level2">'
      +     '</div>'
      +     '<div>'
      +       '<label class="sat-form-label">Teléfono*</label>'
      +       '<input class="sat-form-input" id="sat-f-comp-tel" type="tel" placeholder="" maxlength="" autocomplete="tel-national">'
      +       '<div class="sat-form-error" id="sat-f-comp-tel-err">Sólo números</div>'
      +     '</div>'
      +   '</div>'
      + '</div>';

    /* ── Agregar primer destinatario ── */
    _addRecipientSection(wrapper, true);

    /* ── Botón agregar otro destinatario ── */
    var addBtn = document.createElement('button');
    addBtn.className = 'sat-form-add-recipient';
    addBtn.type = 'button';
    addBtn.textContent = '➕ Agregar otro destinatario';
    addBtn.addEventListener('click', function() {
      _addRecipientSection(wrapper, false);
      scrollToBottom();
    });

    /* ── Botón enviar ── */
    var submitBtn = document.createElement('button');
    submitBtn.className = 'sat-form-submit';
    submitBtn.type = 'button';
    submitBtn.textContent = '✅ Enviar datos';

    var noteDiv = document.createElement('div');
    noteDiv.className = 'sat-form-note';
    noteDiv.textContent = 'Todos los campos son obligatorios';

    /* ── Validación y envío ── */
    submitBtn.addEventListener('click', function() {
      var fields = _collectFormFields(wrapper);
      if (!fields) return;

      /* Deshabilitar formulario */
      submitBtn.disabled = true;
      submitBtn.textContent = '✓ Datos enviados';
      addBtn.style.display = 'none';
      wrapper.querySelectorAll('.sat-form-input').forEach(function(inp) {
        inp.readOnly = true;
        inp.style.background = '#f0ecf5';
        inp.style.cursor = 'default';
      });
      wrapper.querySelectorAll('.sat-form-remove-recipient').forEach(function(btn) {
        btn.style.display = 'none';
      });

      /* Construir mensaje estructurado */
      var msg = '📋 DATOS DE ENTREGA:\n'
        + '📧 Email: ' + fields.email + '\n';

      for (var i = 0; i < fields.recipients.length; i++) {
        var r = fields.recipients[i];
        var label = fields.recipients.length > 1 ? 'DESTINATARIO ' + (i + 1) : 'DESTINATARIO';
        msg += '📍 ' + label + ': ' + r.nombre + ' ' + r.apellido
          + ' | ' + r.dir + ' | ' + r.ciudad + ' | ' + r.tel + '\n';
      }

      msg += '💳 COMPRADOR: ' + fields.compNombre + ' ' + fields.compApellido
        + ' | ' + fields.compDir + ' | ' + fields.compCiudad + ' | ' + fields.compTel;

      sendMessage(msg);
    });

    /* Insertar botones en orden: addBtn → submitBtn → noteDiv */
    var compradorSection = wrapper.querySelector('#sat-f-comp-nombre').closest('.sat-form-section');
    wrapper.insertBefore(addBtn, compradorSection);

    wrapper.appendChild(submitBtn);
    wrapper.appendChild(noteDiv);

    /* Validación en tiempo real: solo dígitos en teléfonos */
    wrapper.addEventListener('input', function(e) {
      if (e.target.classList.contains('sat-form-input') && e.target.type === 'tel') {
        var maxLen = e.target.getAttribute('maxlength');
        if (maxLen) {
          e.target.value = e.target.value.replace(/[^\d]/g, '').slice(0, parseInt(maxLen));
        }
      }
    });

    msgEl.appendChild(wrapper);
    scrollToBottom();
  }

  /* Agrega una sección de destinatario al formulario */
  function _addRecipientSection(wrapper, isFirst) {
    var container = wrapper.querySelector('#sat-recipients-container');
    wrapper._recipientCount = (wrapper._recipientCount || 0) + 1;
    var idx = wrapper._recipientCount;

    var section = document.createElement('div');
    section.className = 'sat-form-section sat-form-recipient';
    section.setAttribute('data-recipient-idx', idx);

    var titleText = isFirst
      ? '📍 Destinatario (quien recibe el regalo)'
      : '📍 Destinatario ' + idx + ' (dirección adicional)';

    section.innerHTML = ''
      + '<div class="sat-form-section__title">' + titleText
      + (isFirst ? '' : '<button type="button" class="sat-form-remove-recipient" title="Eliminar destinatario">✕</button>')
      + '</div>'
      + '<div class="sat-form-row sat-form-row--inline">'
      +   '<div>'
      +     '<label class="sat-form-label">Nombre *</label>'
      +     '<input class="sat-form-input sat-f-dest-nombre" type="text" placeholder="Nombre" autocomplete="off">'
      +   '</div>'
      +   '<div>'
      +     '<label class="sat-form-label">Apellido *</label>'
      +     '<input class="sat-form-input sat-f-dest-apellido" type="text" placeholder="Apellido" autocomplete="off">'
      +   '</div>'
      + '</div>'
      + '<div class="sat-form-row">'
      +   '<label class="sat-form-label">Dirección (incluir apto/interior) *</label>'
      +   '<input class="sat-form-input sat-f-dest-dir" type="text" placeholder="Cra 7 #45-23 Apto 301" autocomplete="off">'
      + '</div>'
      + '<div class="sat-form-row sat-form-row--inline">'
      +   '<div>'
      +     '<label class="sat-form-label">Ciudad *</label>'
      +     '<input class="sat-form-input sat-f-dest-ciudad" type="text" value="Bogotá" readonly style="background:#f0ecf5;cursor:not-allowed;">'
      +   '</div>'
      +   '<div>'
      +     '<label class="sat-form-label">Teléfono (10 dígitos) *</label>'
      +     '<input class="sat-form-input sat-f-dest-tel" type="tel" placeholder="3001234567" maxlength="10" autocomplete="off">'
      +     '<div class="sat-form-error">Debe tener 10 dígitos</div>'
      +   '</div>'
      + '</div>';

    /* Botón eliminar */
    if (!isFirst) {
      var removeBtn = section.querySelector('.sat-form-remove-recipient');
      if (removeBtn) {
        removeBtn.addEventListener('click', function() {
          section.remove();
          _renumberRecipients(wrapper);
        });
      }
    }

    container.appendChild(section);
  }

  /* Renumera los títulos de destinatarios después de eliminar uno */
  function _renumberRecipients(wrapper) {
    var sections = wrapper.querySelectorAll('.sat-form-recipient');
    for (var i = 0; i < sections.length; i++) {
      var titleEl = sections[i].querySelector('.sat-form-section__title');
      if (titleEl) {
        var removeBtn = titleEl.querySelector('.sat-form-remove-recipient');
        var removeBtnHtml = removeBtn ? removeBtn.outerHTML : '';
        if (sections.length === 1) {
          titleEl.innerHTML = '📍 Destinatario (quien recibe el regalo)';
        } else {
          titleEl.innerHTML = '📍 Destinatario ' + (i + 1) + (i === 0 ? ' (quien recibe el regalo)' : ' (dirección adicional)') + removeBtnHtml;
        }
      }
    }
  }

  /* Recolecta y valida todos los campos del formulario */
  function _collectFormFields(wrapper) {
    var emailEl  = wrapper.querySelector('#sat-f-email');
    var cNombre  = wrapper.querySelector('#sat-f-comp-nombre');
    var cApell   = wrapper.querySelector('#sat-f-comp-apellido');
    var cDir     = wrapper.querySelector('#sat-f-comp-dir');
    var cCiudad  = wrapper.querySelector('#sat-f-comp-ciudad');
    var cTel     = wrapper.querySelector('#sat-f-comp-tel');

    var valid = true;

    /* Reset todos */
    wrapper.querySelectorAll('.sat-form-input').forEach(function(inp) {
      inp.classList.remove('sat-form-input--error');
    });
    wrapper.querySelectorAll('.sat-form-error').forEach(function(err) {
      err.style.display = 'none';
    });

    /* Helper: marcar error */
    function markError(el) {
      el.classList.add('sat-form-input--error');
      var errEl = el.parentElement.querySelector('.sat-form-error');
      if (errEl) errEl.style.display = 'block';
      valid = false;
    }

    /* Email */
    var emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRx.test((emailEl.value || '').trim())) {
      markError(emailEl);
    }

    /* Comprador: campos obligatorios */
    [cNombre, cApell, cDir, cCiudad].forEach(function(el) {
      if (!(el.value || '').trim()) markError(el);
    });

    /* Comprador: teléfono */
    if (!(cTel.value || '').trim()) {
      markError(cTel);
    }

    /* Destinatarios */
    var recipientSections = wrapper.querySelectorAll('.sat-form-recipient');
    var recipients = [];

    recipientSections.forEach(function(section) {
      var nombre  = section.querySelector('.sat-f-dest-nombre');
      var apellido = section.querySelector('.sat-f-dest-apellido');
      var dir     = section.querySelector('.sat-f-dest-dir');
      var ciudad  = section.querySelector('.sat-f-dest-ciudad');
      var tel     = section.querySelector('.sat-f-dest-tel');

      /* Campos obligatorios */
      [nombre, apellido, dir].forEach(function(el) {
        if (!(el.value || '').trim()) markError(el);
      });

      /* Teléfono: 10 dígitos */
      if (!/^\d{10}$/.test((tel.value || '').trim())) {
        markError(tel);
      }

      recipients.push({
        nombre: (nombre.value || '').trim(),
        apellido: (apellido.value || '').trim(),
        dir: (dir.value || '').trim(),
        ciudad: (ciudad.value || '').trim(),
        tel: (tel.value || '').trim()
      });
    });

    if (!valid) {
      var firstErr = wrapper.querySelector('.sat-form-input--error');
      if (firstErr) firstErr.focus();
      return null;
    }

    return {
      email: emailEl.value.trim(),
      recipients: recipients,
      compNombre: cNombre.value.trim(),
      compApellido: cApell.value.trim(),
      compDir: cDir.value.trim(),
      compCiudad: cCiudad.value.trim(),
      compTel: cTel.value.trim()
    };
  }

  /* -----------------------------------------------------------------------
     SUBIDA DE FOTOS
     ----------------------------------------------------------------------- */
  function initPhotoUpload() {
    const fileInput = document.getElementById('sat-file-input');
    const btnAttach = document.getElementById('sat-btn-attach');
    const btnRemove = document.getElementById('sat-photo-remove');

    if (btnAttach) {
      btnAttach.addEventListener('click', () => {
        if (fileInput) fileInput.click();
      });
    }

    if (fileInput) {
      fileInput.addEventListener('change', (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validar tipo
        if (!CONFIG.allowedImageTypes.includes(file.type)) {
          addMessage('bot', 'Solo acepto imágenes en formato JPG, PNG o WEBP. ¿Puedes intentar con otro archivo?');
          fileInput.value = '';
          return;
        }

        // Validar tamaño
        if (file.size > CONFIG.maxFileSize) {
          addMessage('bot', 'La imagen es muy pesada (máximo 5MB). ¿Puedes intentar con una más ligera?');
          fileInput.value = '';
          return;
        }

        // Leer como base64
        const reader = new FileReader();
        reader.onload = (ev) => {
          const base64Full = ev.target.result; // data:image/...;base64,XXXX
          const base64Data = base64Full.split(',')[1]; // Solo la parte base64

          state.pendingPhoto = {
            base64: base64Data,
            base64Preview: base64Full,
            filename: file.name,
            mimeType: file.type,
          };

          // AUTO-ENVIAR foto con mensaje "Foto" (UX v2)
          sendMessage('Foto');
        };
        reader.readAsDataURL(file);

        fileInput.value = ''; // Reset para permitir subir el mismo archivo
      });
    }

    if (btnRemove) {
      btnRemove.addEventListener('click', () => {
        state.pendingPhoto = null;
        clearPhotoPreview();
      });
    }
  }

  function showPhotoPreview(dataUrl, filename) {
    const bar = document.getElementById('sat-photo-bar');
    const img = document.getElementById('sat-photo-preview-img');
    const name = document.getElementById('sat-photo-name');

    if (bar) bar.style.display = 'flex';
    if (img) img.src = dataUrl;
    if (name) name.textContent = filename;
  }

  function clearPhotoPreview() {
    const bar = document.getElementById('sat-photo-bar');
    const img = document.getElementById('sat-photo-preview-img');
    if (bar) bar.style.display = 'none';
    if (img) img.src = '';
    state.pendingPhoto = null;
  }

  /* -----------------------------------------------------------------------
     ABRIR / CERRAR PANEL
     ----------------------------------------------------------------------- */
  function openChat() {
    state.isOpen = true;
    const panel = document.getElementById('sat-chat-panel');
    const bubble = document.getElementById('sat-chat-bubble');
    const iconChat = document.getElementById('sat-bubble-icon-chat');
    const iconClose = document.getElementById('sat-bubble-icon-close');
    const badge = document.getElementById('sat-bubble-badge');

    if (panel) panel.classList.add('sat-chat-panel--open');
    if (bubble) bubble.classList.add('sat-chat-bubble--active');
    if (iconChat) iconChat.style.display = 'none';
    if (iconClose) iconClose.style.display = 'block';
    if (badge) badge.style.display = 'none';
    var label = document.getElementById('sat-bubble-label');
    if (label) label.style.display = 'none';

    // Focus en input
    setTimeout(() => {
      const input = document.getElementById('sat-chat-input');
      if (input) input.focus();
    }, 350);

    scrollToBottom();
  }

  function closeChat() {
    state.isOpen = false;
    const panel = document.getElementById('sat-chat-panel');
    const bubble = document.getElementById('sat-chat-bubble');
    const iconChat = document.getElementById('sat-bubble-icon-chat');
    const iconClose = document.getElementById('sat-bubble-icon-close');

    if (panel) panel.classList.remove('sat-chat-panel--open');
    if (bubble) bubble.classList.remove('sat-chat-bubble--active');
    if (iconChat) iconChat.style.display = 'block';
    if (iconClose) iconClose.style.display = 'none';
    var label = document.getElementById('sat-bubble-label');
    if (label) label.style.display = '';
  }

  function toggleChat() {
    if (state.isOpen) {
      closeChat();
    } else {
      openChat();
    }
  }

  /* -----------------------------------------------------------------------
     AUTO-RESIZE TEXTAREA
     ----------------------------------------------------------------------- */
  function initAutoResize() {
    const input = document.getElementById('sat-chat-input');
    if (!input) return;

    input.addEventListener('input', () => {
      input.style.height = 'auto';
      input.style.height = Math.min(input.scrollHeight, 120) + 'px';
      updateSendButton();
    });
  }

  /* -----------------------------------------------------------------------
     MOBILE KEYBOARD HANDLER (iOS)
     Previene que el usuario tenga que hacer pinch-zoom-out después de escribir
     ----------------------------------------------------------------------- */
  function initMobileKeyboardHandler() {
    /* Solo en mobile */
    if (window.innerWidth > 768) return;

    const input = document.getElementById('sat-chat-input');
    if (!input) return;

    /* iOS: Cuando el input recibe focus, scrollear chat al fondo */
    input.addEventListener('focus', function() {
      if (!state.isOpen) return;
      setTimeout(function() {
        scrollToBottom();
      }, 300);
    });

    /* Ajustar panel al viewport visual cuando el teclado abre/cierra */
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', function() {
        if (!state.isOpen) return;
        var panel = document.getElementById('sat-chat-panel');
        if (!panel) return;
        var vh = window.visualViewport.height;
        panel.style.height = vh + 'px';
        panel.style.maxHeight = vh + 'px';
        scrollToBottom();
      });
    }

    /* Restaurar altura al cerrar teclado */
    input.addEventListener('blur', function() {
      setTimeout(function() {
        var panel = document.getElementById('sat-chat-panel');
        if (panel) {
          panel.style.height = '';
          panel.style.maxHeight = '';
        }
      }, 150);
    });
  }

  function updateSendButton() {
    const input = document.getElementById('sat-chat-input');
    const btn = document.getElementById('sat-btn-send');
    if (!input || !btn) return;
    btn.disabled = state.isLoading || (!input.value.trim() && !state.pendingPhoto);
  }

  /* -----------------------------------------------------------------------
     EVENT LISTENERS
     ----------------------------------------------------------------------- */
  function initEventListeners() {
    // Burbuja toggle
    const bubble = document.getElementById('sat-chat-bubble');
    if (bubble) bubble.addEventListener('click', toggleChat);

    // Cerrar panel
    const closeBtn = document.getElementById('sat-chat-close');
    if (closeBtn) closeBtn.addEventListener('click', closeChat);

    // Enviar mensaje
    const sendBtn = document.getElementById('sat-btn-send');
    if (sendBtn) {
      sendBtn.addEventListener('click', () => {
        const input = document.getElementById('sat-chat-input');
        if (input) sendMessage(input.value);
      });
    }

    // Enter para enviar (Shift+Enter para nueva línea)
    const input = document.getElementById('sat-chat-input');
    if (input) {
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          sendMessage(input.value);
        }
      });
    }

    // Cerrar con Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && state.isOpen) {
        closeChat();
      }
    });
  }

  /* -----------------------------------------------------------------------
     API PÚBLICA: window.SATChat
     ----------------------------------------------------------------------- */
  window.SATChat = {
    /**
     * Abre el chat sin contexto
     */
    abrir: function () {
      openChat();
    },

    /**
     * Abre el chat con contexto de un producto (desde botón "Lo quiero 💬")
     * @param {HTMLElement} buttonEl - El botón con data-attributes del producto
     */
    abrirConProducto: function (buttonEl) {
      const title = buttonEl.dataset.productTitle;
      const price = buttonEl.dataset.productPrice;
      const variantId = buttonEl.dataset.variantId;
      const image = buttonEl.dataset.productImage;

      openChat();

      // Pequeño delay para que el panel esté visible
      setTimeout(() => {
        // Enviar mensaje automático simulando que el usuario eligió
        const mensaje = `Me interesa el ${title}`;
        sendMessage(mensaje);
      }, 400);
    },

    /**
     * Abre el chat con datos directos (para tarjetas de producto dentro del chat)
     */
    abrirConDatos: function (title, price, variantId, image) {
      const mensaje = `Me interesa el ${title}`;
      sendMessage(mensaje);
    },

    /**
     * Cierra el chat
     */
    cerrar: function () {
      closeChat();
    },

    /**
     * Envía un mensaje programáticamente
     */
    enviar: function (texto) {
      if (!state.isOpen) openChat();
      setTimeout(() => sendMessage(texto), 300);
    },
  };

  /* -----------------------------------------------------------------------
     INICIALIZACIÓN
     ----------------------------------------------------------------------- */
  function init() {
    // Cargar sesión previa
    loadSession();

    // Generar session ID si no existe
    if (!state.sessionId) {
      state.sessionId = generateSessionId();
      saveSession();
    }

    // Inyectar HTML
    injectHTML();

    // Inicializar listeners
    initEventListeners();
    initAutoResize();
    initPhotoUpload();
    initMobileKeyboardHandler();

    // Renderizar historial si existe
    if (state.messages.length > 0) {
      renderAllMessages();
    } else {
      // Mensaje de bienvenida
      addMessage('bot', CONFIG.welcomeMessage);
    }

    // Auto-abrir si configurado
    if (CONFIG.autoOpenDelay) {
      setTimeout(openChat, CONFIG.autoOpenDelay);
    }

    // Mostrar badge de notificación si el chat no se ha abierto
    const badge = document.getElementById('sat-bubble-badge');
    if (badge && state.messages.length <= 1) {
      badge.style.display = 'block';
    }

    console.log('[SAT Chat] Widget inicializado. Session:', state.sessionId);
  }

  /* -----------------------------------------------------------------------
     ARRANQUE
     ----------------------------------------------------------------------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();